import React from 'react';

/**
 * Vistas previas diseñadas en código de los tipos de página del pack
 * (según Índices - DPS65). Se usan en "A look inside" mientras no haya
 * capturas reales de Gamma en data/assets.ts.
 */

const checkpoints = ['SYMPTOM', 'CODE', 'SPLIT', 'PROVE', 'FIX & FILE'];
function PageShell({
  band,
  label,
  title,
  children
}: {
  band: string;
  label: string;
  title: string;
  children: React.ReactNode;
}) {
  return <div className="flex h-full w-full flex-col bg-paper text-left text-pine" data-id="element-168" data-mp="pd3w2rue2j8z">
      <div className={`h-1.5 w-full flex-none ${band}`} data-id="element-169" data-mp="kg58ks58dajr" />
      <div className="flex items-center justify-between gap-2 px-3 pt-2" data-id="element-170" data-mp="khojs4c14veo">
        <span className="truncate font-mono text-[8px] font-bold uppercase tracking-wider text-pine-muted" data-id="element-171" data-mp="yebvjd8n5q4n">
          {label}
        </span>
        <span className="hidden flex-none font-mono text-[7px] uppercase tracking-wider text-pine-muted sm:inline" data-id="element-172" data-mp="30684qg7c6nw9">
          Diagnostic Pro
        </span>
      </div>
      <p className="px-3 pt-0.5 font-display text-[14px] leading-tight text-pine" data-id="element-173" data-mp="2v9hs62uqqafl">
        {title}
      </p>
      <div className="min-h-0 flex-1 overflow-hidden px-3 pb-2.5 pt-1.5" data-id="element-174" data-mp="340opmorqxhww">
        {children}
      </div>
    </div>;
}
function Block({
  k,
  text,
  mono = false
}: {
  k: string;
  text: string;
  mono?: boolean;
}) {
  return <div className="rounded-md bg-paper-dark px-1.5 py-1" data-id="element-175" data-mp="3ir5t0dnxyhwf">
      <span className="block font-mono text-[7px] font-bold text-forest" data-id="element-176" data-mp="3isqms12flwt8">{k}</span>
      <span className={`block text-[7.5px] leading-snug text-pine-soft ${mono ? 'font-mono' : ''}`} data-id="element-177" data-mp="3mshk6nmgrb6q">
        {text}
      </span>
    </div>;
}
export function CardPreview() {
  return <PageShell band="bg-vol-2" label="Card 2.12 · GT · Array zone" title="Ground Fault Alarm: Find Which Leg and Which String" data-id="element-178" data-mp="mc81zwc9its">
      <div className="flex gap-[3px]" data-id="element-179" data-mp="22dflsy1izdik">
        {checkpoints.map((c, i) => <span key={c} className={`flex-1 truncate rounded-[3px] py-[3px] text-center font-mono text-[6px] font-bold ${i === 3 ? 'bg-ember text-pine' : 'bg-forest text-paper'}`} data-id="element-180" data-mp="22filyawe6n10" data-mpi={i}>
            {c}
          </span>)}
      </div>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-id="element-181" data-mp="1ueui05wald3u">
        <Block k="SPLIT" text="Open the DC isolator, confirm 0 A with the DC clamp, then separate the strings." data-id="element-182" data-mp="29aihxiukve1x" />
        <Block k="PROVE" text="Insulation test on each isolated string, + to earth then − to earth. Discharge after." data-id="element-183" data-mp="24d0gsemsy34h" />
        <Block k="EXPECT" mono text="Faulted string far below its neighbors. Pass value: verify in the manual." data-id="element-184" data-mp="2daaus7r086d3" />
        <Block k="FIX & FILE" text="Halve the string to the faulted run. Log on WS-10." data-id="element-185" data-mp="2s22zlpt6a20k" />
      </div>
      <p className="mt-1.5 rounded bg-signal/10 px-1.5 py-1 text-[7px] font-semibold text-signal" data-id="element-186" data-mp="310wosv46bq43">
        The array is live in daylight. Never break a DC connector under load.
      </p>
    </PageShell>;
}
function Node({
  children,
  tone = 'base'
}: {
  children: React.ReactNode;
  tone?: 'base' | 'alert' | 'card';
}) {
  const styles = {
    base: 'bg-paper-dark text-pine',
    alert: 'bg-ember text-pine',
    card: 'bg-forest text-paper'
  };
  return <span className={`block rounded-md px-1.5 py-1 text-center text-[7.5px] font-semibold leading-tight ${styles[tone]}`} data-id="element-187" data-mp="36m5qdoosrtn5">
      {children}
    </span>;
}
function Arrow() {
  return <span className="block text-center font-mono text-[8px] leading-none text-pine-muted" data-id="element-188" data-mp="2wwccui53h4g9">↓</span>;
}
export function FlowchartPreview() {
  return <PageShell band="bg-vol-2" label="Volume 2 · Page 43 · Flowchart" title="Low Production: From Portal Alert to the Faulty String" data-id="element-189" data-mp="2nwfuszd7n3u6">
      <div className="mx-auto grid max-w-[92%] gap-[3px]" data-id="element-190" data-mp="2rykkxo4g9ic2">
        <Node tone="alert" data-id="element-191" data-mp="2dxqaz11r79ba">Portal alert: low production</Node>
        <Arrow data-id="element-192" data-mp="2e0cs9seg536p" />
        <Node data-id="element-193" data-mp="2ixcyq4f7k7yc">Are all strings low?</Node>
        <Arrow data-id="element-194" data-mp="2iwbcr4wnm11u" />
        <div className="grid grid-cols-2 gap-1.5" data-id="element-195" data-mp="2iwth6otgzlku">
          <Node tone="card" data-id="element-196" data-mp="3laiy2yn4j6z2">YES → Card 2.02 Soiling, shade or expectations</Node>
          <Node tone="card" data-id="element-197" data-mp="3lclpnn3xd85w">NO → Card 2.01 Isolate the weak string</Node>
        </div>
        <Arrow data-id="element-198" data-mp="3hba4puvzxb41" />
        <Node data-id="element-199" data-mp="3cih1jbg6eau2">Voc vs neighbor string and temp-corrected label</Node>
        <Arrow data-id="element-200" data-mp="3cgmfftt64coe" />
        <div className="grid grid-cols-2 gap-1.5" data-id="element-201" data-mp="3cg49g5gpek3a">
          <Node tone="card" data-id="element-202" data-mp="37ib5zk6sc46p">Low Voc → diodes and modules (2.08)</Node>
          <Node tone="card" data-id="element-203" data-mp="32k1af8h5f1kk">Voc OK, low current → shade, mismatch (2.11)</Node>
        </div>
      </div>
    </PageShell>;
}
export function CodeColumnsPreview() {
  const rows = [['PV DC wiring', 'NEC 690', 'C22.1 §64', 'BS 7671 §712', 'AS/NZS 5033'], ['Interconnection', 'NEC 705', 'C22.1 §64', 'BS 7671', 'AS/NZS 4777'], ['Battery location', 'NEC 706', 'C22.1 §64', 'BS 7671 Ch 57', 'AS/NZS 5139'], ['Rapid shutdown', 'Required', 'Required', 'Not required', 'Not required']];
  return <PageShell band="bg-vol-5" label="Volume 5 · Code Column Method" title="One Check, Four Countries" data-id="element-204" data-mp="mslt3cpn6sgn">
      <div className="overflow-hidden rounded-md border border-paper-dark" data-id="element-205" data-mp="ubv2h5y5er5p">
        <div className="grid grid-cols-5 bg-pine font-mono text-[6.5px] font-bold text-paper" data-id="element-206" data-mp="zaz9pr1iiete">
          {['CHECK', 'US', 'CA', 'UK', 'AU'].map(h => <span key={h} className="px-1 py-1" data-id="element-207" data-mp="144k7pj33v8sl">
              {h}
            </span>)}
        </div>
        {rows.map(row => <div key={row[0]} className="grid grid-cols-5 border-t border-paper-dark bg-white" data-id="element-208" data-mp="belh3j99j3lt">
            {row.map((cell, i) => <span key={i} className={`px-1 py-1 text-[6.5px] leading-tight ${i === 0 ? 'font-semibold text-pine' : 'font-mono text-pine-soft'}`} data-id="element-209" data-mp="gdet178wimjv" data-mpi={i}>
                {cell}
              </span>)}
          </div>)}
      </div>
      <p className="mt-1.5 font-mono text-[6.5px] text-pine-muted" data-id="element-210" data-mp="1wthfgs9oxn73">
        Cited by number, never copied. Check the edition adopted in your jurisdiction.
      </p>
    </PageShell>;
}
export function WorksheetPreview() {
  const cols = ['STRING', 'Voc', 'LABEL Voc*', 'I (A)', 'R iso', 'P/F'];
  return <PageShell band="bg-vol-1" label="Worksheet WS-04" title="String Measurement Log" data-id="element-211" data-mp="aood2jgsfkhh">
      <div className="grid grid-cols-3 gap-1 font-mono text-[6.5px] text-pine-muted" data-id="element-212" data-mp="amk7zy75kffm">
        <span className="border-b border-pine-muted/40 pb-0.5" data-id="element-213" data-mp="5qd0i0z3yvvk">ORDER # ____</span>
        <span className="border-b border-pine-muted/40 pb-0.5" data-id="element-214" data-mp="3e5wskwo1m891">IRRAD. ____ W/m²</span>
        <span className="border-b border-pine-muted/40 pb-0.5" data-id="element-215" data-mp="3a4tcqa5zwc7t">MODULE T ____ °C</span>
      </div>
      <div className="mt-1.5 overflow-hidden rounded-md border border-paper-dark" data-id="element-216" data-mp="3nw871d5crzu6">
        <div className="grid grid-cols-6 bg-forest font-mono text-[6px] font-bold text-paper" data-id="element-217" data-mp="3j482f7ec8ir9">
          {cols.map(c => <span key={c} className="px-1 py-1" data-id="element-218" data-mp="2vdkpg7pkf2xu">
              {c}
            </span>)}
        </div>
        {[1, 2, 3, 4].map(r => <div key={r} className="grid h-[13px] grid-cols-6 border-t border-paper-dark bg-white" data-id="element-219" data-mp="15xrzovdiz9rb">
            <span className="px-1 font-mono text-[6.5px] leading-[13px] text-pine-soft" data-id="element-220" data-mp="1aogpm69k3yoc">S{r}</span>
          </div>)}
      </div>
      <p className="mt-1 font-mono text-[6px] text-pine-muted" data-id="element-221" data-mp="wy4wq555dvw5">
        *Temperature-corrected. Customers by initials or order number only.
      </p>
    </PageShell>;
}
export function GuessProvePreview() {
  const guess = ['Sees an isolation fault', 'Swaps the inverter', 'Fault is back next morning', 'Second truck roll'];
  const prove = ['Reads the event log first', 'Splits array from inverter', 'Insulation test finds string 3', 'Fixes the connector, files the report'];
  return <PageShell band="bg-vol-1" label="Volume 1 · Comparison" title="Box Swapper vs Diagnostician: the Same Call" data-id="element-222" data-mp="1klv6u83b0rhp">
      <div className="grid grid-cols-2 gap-1.5" data-id="element-223" data-mp="473axl6y923n">
        <div className="rounded-md bg-signal px-1.5 py-1.5 text-white" data-id="element-224" data-mp="980spwo1ru4m">
          <span className="block font-mono text-[7px] font-bold" data-id="element-225" data-mp="954wqdvrffit">GUESS</span>
          {guess.map(g => <span key={g} className="mt-1 block text-[7px] leading-tight" data-id="element-226" data-mp="vvzcj0ffxcnz">
              ✕ {g}
            </span>)}
        </div>
        <div className="rounded-md bg-moss px-1.5 py-1.5 text-white" data-id="element-227" data-mp="qxxrwb2d95ae">
          <span className="block font-mono text-[7px] font-bold" data-id="element-228" data-mp="ly1r0koyttz2">PROVE</span>
          {prove.map(p => <span key={p} className="mt-1 block text-[7px] leading-tight" data-id="element-229" data-mp="1jm3hbb4d7igk">
              ✓ {p}
            </span>)}
        </div>
      </div>
    </PageShell>;
}
export function FieldCardPreview() {
  const steps = ['AC', 'DC', 'Battery', 'Verify'];
  const rules = ['Never break a DC connector or fuse under load', 'Isc only with a dedicated PV tester', 'Rapid shutdown does not de-energize the array', 'Discharge the string after an insulation test'];
  return <PageShell band="bg-vol-1" label="Volume 1 · Page 50 · Field card" title="Solar Service Field Card" data-id="element-230" data-mp="1xkta4ek5ivz4">
      <span className="font-mono text-[6.5px] font-bold text-forest" data-id="element-231" data-mp="25vagfoqg8ewx">
        SAFE-APPROACH SEQUENCE
      </span>
      <div className="mt-0.5 flex items-center gap-1" data-id="element-232" data-mp="29w2oienl4drm">
        {steps.map((s, i) => <React.Fragment key={s} data-id="element-233" data-mp="2etvt5398ozps" data-mpi={i}>
            <span className="flex-1 rounded-md bg-ember py-1 text-center font-mono text-[7px] font-bold text-pine" data-id="element-234" data-mp="2ex07uukbzdup">
              {i + 1} {s}
            </span>
            {i < steps.length - 1 && <span className="text-[7px] text-pine-muted" data-id="element-235" data-mp="2oqqv5w4q1i8t">→</span>}
          </React.Fragment>)}
      </div>
      <span className="mt-1.5 block font-mono text-[6.5px] font-bold text-signal" data-id="element-236" data-mp="2ss2btc5mat4v">STOP RULES</span>
      <ul className="mt-0.5 grid gap-[2px]" data-id="element-237" data-mp="2xnho3znz8gz6">
        {rules.map(r => <li key={r} className="text-[7px] leading-tight text-pine-soft" data-id="element-238" data-mp="32jgf7v0owd3a">
            ■ {r}
          </li>)}
      </ul>
    </PageShell>;
}